import dayjs from 'dayjs';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import useSWR from 'swr';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from '@/components/ui/toast';

type ScanConfirmationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  checkpoint: {
    id: string;
    name: string;
  };
  number: number;
};

const ScanConfirmationModal = ({
  isOpen,
  onClose,
  checkpoint,
  number,
}: ScanConfirmationModalProps) => {
  const { data, isValidating, mutate } = useSWR(
    `/api/participantFromNumber?number=${number}`,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const sendRequest = async () => {
    setIsSubmitting(true);

    const response = await fetch('/api/checkInParticipant', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        checkpoint: checkpoint.id,
        participant: data.id,
      }),
    });

    if (response.ok) {
      toast({
        title: 'Check-In erfolgreich!',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } else {
      toast({
        title: 'Check-In fehlgeschlagen!',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }

    setIsSubmitting(false);
    mutate();
    onClose();
  };

  const handleDeleteCheckIn = async (checkIn: {
    id: string;
    loggedAt: string;
    checkpoint: { name: string };
  }) => {
    const time = dayjs(checkIn.loggedAt).format('DD.MM.YYYY, HH:mm');
    const confirmed = window.confirm(
      `Check-In um ${time} Uhr bei "${checkIn.checkpoint.name}" wirklich löschen?\n\n` +
        'Die Gesamtdistanz des Teilnehmers und die Sponsorenbeträge werden ' +
        'neu berechnet.',
    );
    if (!confirmed) return;

    setDeletingId(checkIn.id);

    try {
      const response = await fetch('/api/deleteCheckIn', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: checkIn.id }),
      });

      if (response.ok) {
        toast({
          title: 'Check-In gelöscht!',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        mutate();
      } else {
        toast({
          title: 'Löschen fehlgeschlagen!',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch {
      toast({
        title: 'Löschen fehlgeschlagen!',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Check-In bestätigen</DialogTitle>
        </DialogHeader>
        <div>
          {(!data && !isValidating) || (data && data.success === false) ? (
            <p>Ein Fehler ist aufgetreten! Bitte Angaben überprüfen!</p>
          ) : !data ? (
            <div className="flex justify-center">
              <Spinner size="xl" />
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <h2 className="text-lg font-semibold">Check-In Daten</h2>

              <div className="w-full">
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell className="text-sm">Startnummer</TableCell>
                      <TableCell className="text-sm">
                        <b>{data.number}</b>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-sm">Läufer</TableCell>
                      <TableCell className="text-sm">
                        <b>
                          {data.firstName} {data.lastName}
                        </b>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-sm">Stamm</TableCell>
                      <TableCell className="text-sm">
                        <b>{data.group.name}</b>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-sm">Checkpoint</TableCell>
                      <TableCell className="text-sm">
                        <b>{checkpoint.name}</b>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              {data.checkIns.length > 0 && (
                <>
                  <h2 className="text-lg font-semibold">Verlauf</h2>

                  <div className="mt-4 w-full">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Uhrzeit</TableHead>
                          <TableHead>Checkpoint</TableHead>
                          <TableHead className="w-12" />
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.checkIns.map((c: Record<string, any>) => (
                          <TableRow key={c.id}>
                            <TableCell>
                              {dayjs(c.loggedAt).format('DD.MM.YYYY, HH:mm')}{' '}
                              Uhr
                            </TableCell>
                            <TableCell>
                              <b>{c.checkpoint.name}</b>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-red-500 hover:text-red-600"
                                disabled={deletingId === c.id || isSubmitting}
                                onClick={() =>
                                  handleDeleteCheckIn({
                                    id: c.id,
                                    loggedAt: c.loggedAt,
                                    checkpoint: c.checkpoint,
                                  })
                                }
                                aria-label="Check-In löschen"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            className="mr-3"
            disabled={isSubmitting}
            onClick={onClose}
          >
            Abbrechen
          </Button>
          {data && data.success !== false && (
            <Button disabled={isSubmitting} onClick={sendRequest}>
              {isSubmitting ? 'Wird gesendet...' : 'Bestätigen'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ScanConfirmationModal;
